/**
 * Cache Configuration Module - IPHUB Project
 * Centralized configuration for cache system
 */

/**
 * Default cache configuration
 */
export const DEFAULT_CONFIG = {
  prefix: "iphub_",
  version: "2.0",
  storage: {
    maxSize: 5 * 1024 * 1024, // 5MB
    maxItemSize: 1 * 1024 * 1024, // 1MB per item
    cleanupThreshold: 0.8, // Cleanup at 80% usage
  },
  ttl: {
    default: 30 * 60 * 1000, // 30 minutes
    short: 10 * 60 * 1000, // 10 minutes
    medium: 60 * 60 * 1000, // 1 hour
    long: 12 * 60 * 60 * 1000, // 12 hours
    persistent: null, // No expiration
  },
  debug: false,
};

/**
 * TTL constants for common use cases
 */
export const TTL = {
  SHORT: 10 * 60 * 1000, // 10 minutes
  MEDIUM: 60 * 60 * 1000, // 1 hour
  LONG: 12 * 60 * 60 * 1000, // 12 hours
  PERSISTENT: null, // No expiration
};

/**
 * Storage size constants
 */
export const STORAGE = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_ITEM_SIZE: 1 * 1024 * 1024, // 1MB per item
  CLEANUP_THRESHOLD: 0.8, // 80% usage
};

/**
 * Cache prefixes for different namespaces
 */
export const PREFIXES = {
  DEFAULT: "iphub_",
  FDI: "fdi",
  USER: "user",
  VIEWPORT: "viewport",
  MARKERS: "markers",
  BOUNDARIES: "boundaries",
};

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  STORAGE_UNAVAILABLE: "localStorage not available, caching disabled",
  ITEM_TOO_LARGE: "Item too large for cache",
  INSUFFICIENT_SPACE: "Insufficient storage space",
  CORRUPTED_DATA: "Corrupted cache data detected",
};

/**
 * Debug messages
 */
export const DEBUG_MESSAGES = {
  INITIALIZED: "SimpleCache: Initialized with {size} bytes used",
  MEMORY_HIT: "SimpleCache: Memory HIT for {key}",
  STORAGE_HIT: "SimpleCache: Storage HIT for {key}",
  CACHED: "SimpleCache: Cached {key} ({size}KB)",
  DELETED: "SimpleCache: Deleted {key}",
  CLEARED: "SimpleCache: Cleared {count} items{prefix}",
  CLEANED_UP: "SimpleCache: Cleaned up {count} expired items",
  REMOVED_OLDEST: "SimpleCache: Removed {count} oldest items to free {size} bytes",
  STATS_RESET: "SimpleCache: Statistics reset",
};

/**
 * Create custom configuration by merging with defaults
 * @param {Object} customConfig - Custom configuration to merge
 * @returns {Object} Merged configuration
 */
export function createConfig(customConfig = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...customConfig,
    storage: {
      ...DEFAULT_CONFIG.storage,
      ...customConfig.storage,
    },
    ttl: {
      ...DEFAULT_CONFIG.ttl,
      ...customConfig.ttl,
    },
  };
}

/**
 * Validate configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export function validateConfig(config) {
  const errors = [];

  if (config.storage?.maxSize <= 0) {
    errors.push("maxSize must be greater than 0");
  }

  if (config.storage?.maxItemSize <= 0) {
    errors.push("maxItemSize must be greater than 0");
  }

  if (config.storage?.maxItemSize > config.storage?.maxSize) {
    errors.push("maxItemSize cannot be greater than maxSize");
  }

  if (config.storage?.cleanupThreshold <= 0 || config.storage?.cleanupThreshold >= 1) {
    errors.push("cleanupThreshold must be between 0 and 1");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
